import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Button, Input, Label, Table } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import { Pagination } from "../../DataTable";
import { COLOR_ESTADO, URL_FINANZAS, auth, fmt } from "./finanzasUtils";

const POR_PAGINA = 50;
const ESTADOS = ["Con saldo", "Liquidado", "Sin cargos"];

// Control por contenedor: cargos, abonos, gastos y pagos en un renglón, con las cuatro altas a la mano
export default function TabContenedores({ user, accion, verDetalle, refresco }) {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [inicio, setInicio] = useState(moment().subtract(91, "days").format("YYYY-MM-DD"));
  const [fin, setFin] = useState(moment().format("YYYY-MM-DD"));
  const [fCliente, setFCliente] = useState("");
  const [fBroker, setFBroker] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    setCargando(true);
    axios
      .get(`${URL_FINANZAS}Contenedores/${inicio}/${fin}`, auth())
      .then(({ data }) => setFilas(data))
      .catch((err) => {
        setFilas([]);
        Swal.fire({ icon: "error", title: "No se pudieron cargar los contenedores", text: err?.response?.data?.message || err.message });
      })
      .finally(() => setCargando(false));
  }, [inicio, fin, refresco]);

  const opciones = (campo) => [...new Set(filas.map((f) => f[campo]).filter(Boolean))].sort();
  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return filas.filter(
      (f) =>
        (!fCliente || f.cliente === fCliente) &&
        (!fBroker || f.broker === fBroker) &&
        (!fEstado || f.estado === fEstado) &&
        (!q || [f.referencia, f.numeroReferencia, f.cliente, f.broker].some((x) => String(x || "").toLowerCase().includes(q)))
    );
  }, [filas, fCliente, fBroker, fEstado, busca]);

  const suma = (campo) => visibles.reduce((s, f) => s + f[campo], 0);

  const excel = () => {
    const datos = visibles.map((f) => ({
      Numero: f.numero, Contenedor: f.referencia, Referencia: f.numeroReferencia, Cliente: f.cliente, Broker: f.broker, ETA: f.eta,
      Notas: f.notas, Liberacion: f.liberacion, Flete: f.flete, Cargado: f.cargado, Abonado: f.abonado, Saldo: f.saldo,
      Gastos: f.gastos, Pagado: f.pagado, PorPagar: f.porPagar, Utilidad: f.utilidad, Estado: f.estado,
    }));
    const cols = Object.keys(datos[0] || { Numero: "" });
    new ExportJsonExcel({ fileName: `ControlContenedores-${inicio}-${fin}`, datas: [{ sheetData: datos, sheetName: "Control", sheetFilter: cols, sheetHeader: cols }] }).saveExcel();
  };

  const select = (label, valor, setValor, lista, todos = "Todos") => (
    <div>
      <Label>{label}</Label>
      <Input bsSize="sm" type="select" value={valor} onChange={(e) => { setValor(e.target.value); setPagina(1); }}>
        <option value="">{todos}</option>
        {lista.map((x) => <option key={x}>{x}</option>)}
      </Input>
    </div>
  );

  return (
    <>
      <div className="filter-bar">
        <div>
          <Label>Fecha inicio</Label>
          <Input bsSize="sm" type="date" value={inicio} onChange={(e) => { setInicio(e.target.value); setPagina(1); }} />
        </div>
        <div>
          <Label>Fecha fin</Label>
          <Input bsSize="sm" type="date" value={fin} onChange={(e) => { setFin(e.target.value); setPagina(1); }} />
        </div>
        {select("Cliente", fCliente, setFCliente, opciones("cliente"))}
        {select("Broker", fBroker, setFBroker, opciones("broker"))}
        {select("Estado", fEstado, setFEstado, ESTADOS)}
        <div>
          <Label>Buscar</Label>
          <Input bsSize="sm" type="text" value={busca} onChange={(e) => { setBusca(e.target.value); setPagina(1); }} />
        </div>
        <div className="fz-excel">
          <Button size="sm" onClick={excel} disabled={!visibles.length}><i className="far fa-file-excel" /> Excel</Button>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-chip"><i className="fas fa-ship" style={{ color: "#2563eb" }} /> Contenedores <span>{new Intl.NumberFormat("en-US").format(visibles.length)}</span></div>
        <div className="stat-chip"><i className="fas fa-file-invoice-dollar" style={{ color: "#2563eb" }} /> Cargado <span>{fmt(suma("cargado"))}</span></div>
        <div className="stat-chip"><i className="fas fa-hand-holding-usd" style={{ color: "#16a34a" }} /> Abonado <span>{fmt(suma("abonado"))}</span></div>
        <div className="stat-chip"><i className="fas fa-balance-scale" style={{ color: "#b91c1c" }} /> Por cobrar <span>{fmt(suma("saldo"))}</span></div>
        <div className="stat-chip"><i className="fas fa-receipt" style={{ color: "#ea580c" }} /> Gastos <span>{fmt(suma("gastos"))}</span></div>
        <div className="stat-chip"><i className="fas fa-money-check-alt" style={{ color: "#b91c1c" }} /> Por pagar <span>{fmt(suma("porPagar"))}</span></div>
      </div>

      {cargando ? (
        <div className="rc-state"><i className="fas fa-spinner fa-spin" /><div>Cargando contenedores…</div></div>
      ) : visibles.length === 0 ? (
        <div className="rc-state"><i className="fas fa-box-open" /><div>No hay contenedores con estos filtros.</div></div>
      ) : (
        <>
          <div className="table-wrapper">
            <Table size="sm" hover className="table-responsive-xl">
              <thead>
                <tr>
                  <th>Número</th><th>Contenedor</th><th>Referencia</th><th>Cliente</th><th>Broker</th><th>ETA</th>
                  <th className="td-num">Notas</th><th className="td-num">Liberación</th><th className="td-num">Flete</th>
                  <th className="td-num">Abonado</th><th className="td-num">Saldo</th><th>Estado</th>
                  <th className="td-num">Gastos</th><th className="td-num">Pagado</th><th className="td-num">Por pagar</th>
                  <th className="td-num">Utilidad</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibles.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA).map((f) => (
                  <tr key={f._id}>
                    <td>{f.numero}</td>
                    <td className="td-strong">{f.referencia}</td>
                    <td>{f.numeroReferencia}</td>
                    <td>{f.cliente}</td>
                    <td>{f.broker}</td>
                    <td>{f.eta}</td>
                    <td className="td-num td-money">{fmt(f.notas)}</td>
                    <td className="td-num td-money">{fmt(f.liberacion)}</td>
                    <td className="td-num td-money">{fmt(f.flete)}</td>
                    <td className="td-num td-money">{fmt(f.abonado)}</td>
                    <td className="td-num td-money">{fmt(f.saldo)}</td>
                    <td><span className={`badge-soft badge-soft-${COLOR_ESTADO[f.estado]}`}>{f.estado}</span></td>
                    <td className="td-num td-money">{fmt(f.gastos)}</td>
                    <td className="td-num td-money">{fmt(f.pagado)}</td>
                    <td className="td-num td-money">{fmt(f.porPagar)}</td>
                    <td className="td-num td-money">{fmt(f.utilidad)}</td>
                    <td className="fz-botones">
                      <Button size="sm" color="secondary" title="Ver detalle" onClick={() => verDetalle(f)}><i className="fas fa-eye" /></Button>
                      {user.menuAdmin ? <Button size="sm" color="info" onClick={() => accion("cargos", f)}>Cargo</Button> : null}
                      {user.abonosCreate ? <Button size="sm" color="success" onClick={() => accion("abonos", f)}>Abono</Button> : null}
                      {user.gastosCreate ? <Button size="sm" color="primary" onClick={() => accion("gastos", f)}>Gasto</Button> : null}
                      {user.pagosCreate ? <Button size="sm" color="warning" onClick={() => accion("pagos", f)}>Pago</Button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Pagination total={visibles.length} itemsPerPage={POR_PAGINA} currentPage={pagina} onPageChange={setPagina} />
        </>
      )}
    </>
  );
}
